/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Flex,
  Space,
  Switch,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  InputChildRef,
  InputWithValid,
} from 'src/components/Common/Form/InputWithValid';
import CSpinner from 'src/components/CSpinner';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../../components/Common/ErrorAlert';
import { TextareaWithValid } from '../../../../components/Common/Form/TextareaWithValid';
import { useSiderWidthStore } from '../../../../models/useSiderWidthModel';
import { ValidProps } from '../../../../types';
import { validateIsNotEmptyString } from '../../../../utils/validate';
const NotificationEmailSettings = (props: {
  withEditPermission: boolean;
  customizedDisplayNameEnabled: boolean;
}) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [skipNotificationOnPassStatus, setSkipNotificationOnPassStatus] =
    useState(false);
  const [signatureContentStatus, setSignatureContentStatus] = useState(false);
  const [senderDisplayNameStatus, setSenderDisplayNameStatus] = useState(false);
  const [signatureContent, setSignatureContent] = useState('');
  const [senderDisplayName, setSenderDisplayName] = useState('');
  const { siderWidth } = useSiderWidthStore();
  useEffect(() => {
    (async function doCall() {
      try {
        setLoading(true);
        const res = await GlobalApi.getNotificationEmailSettings();
        // setData(res.data);
        setSkipNotificationOnPassStatus(
          res.data.skipNotificationOnPass?.enabledStatus ?? false
        );
        setSignatureContentStatus(res.data.signatureContent.enabledStatus);
        setSignatureContent(res.data.signatureContent.content);
        setSenderDisplayName(res.data.senderDisplayName.content);
        setSenderDisplayNameStatus(res.data.senderDisplayName.enabledStatus);
      } catch (err) {
        setErrorMsg(t('common.apiException.internalError'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function onChangeSignatureContentStatus(e: any) {
    const status = e.target.checked;
    setSignatureContentStatus(status);
    if (!status) {
      await textareaChildRef.current?.reset();
    }
  }

  async function onChangeSenderDisplayNameStatus(e: any) {
    const status = e.target.checked;
    setSenderDisplayNameStatus(status);
    if (!status) {
      await inputChildRef.current?.reset();
    }
  }

  async function onSave() {
    if (props.customizedDisplayNameEnabled && senderDisplayNameStatus) {
      await inputChildRef.current?.validate(senderDisplayName);
      if (inputChildRef.current?.errorMsg !== undefined) return;
    }
    await inputChildRef.current?.reset();
    if (signatureContentStatus) {
      await textareaChildRef.current?.validate(signatureContent);
      if (textareaChildRef.current?.errorMsg !== undefined) return;
    }
    await textareaChildRef.current?.reset();
    const payload = {
      skipNotificationOnPass: {
        enabledStatus: skipNotificationOnPassStatus,
      },
      signatureContent: {
        content: signatureContent,
        enabledStatus: signatureContentStatus,
      },
      senderDisplayName: {
        content: senderDisplayName,
        enabledStatus: senderDisplayNameStatus,
      },
    };
    try {
      setLoading(true);
      await GlobalApi.updateNotificationEmailSettings(payload);
      window.showNotification({
        titleMsg: t('globalSetting.notificationEmailSettings.save.success'),
        severity: 'success',
      });
    } catch (err) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }
  const inputChildRef = useRef<InputChildRef>(null);
  const textareaChildRef = useRef<InputChildRef>(null);
  const textareaValidates: ValidProps[] = [
    {
      validMethod: (value: string) => {
        if (!signatureContentStatus) return true;

        return validateIsNotEmptyString(value);
      },
      errMsg: t(
        'globalSetting.notificationEmailSettings.signatureContent.error.empty'
      ),
    },
  ];
  const inputValidates: ValidProps[] = [
    {
      validMethod: (value: string) => {
        if (!senderDisplayNameStatus) return true;

        return validateIsNotEmptyString(value);
      },
      errMsg: t(
        'globalSetting.notificationEmailSettings.senderDisplayName.error.empty'
      ),
    },
  ];
  return (
    <Box>
      {loading && <CSpinner />}
      <Box>
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text>
            <Space> {t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            {t(
              'globalSetting.notificationEmailSettings.notificationBehavior.title'
            )}
          </Text>
        </Flex>
        <Flex alignItems="flex-start">
          <Switch
            pr="1x"
            id="skipNotificationOnPass"
            checked={skipNotificationOnPassStatus}
            onChange={(e: any) =>
              setSkipNotificationOnPassStatus(e.target.checked)
            }
          />
          <Box>
            <label htmlFor="skipNotificationOnPass">
              {t(
                'globalSetting.notificationEmailSettings.notificationBehavior.description'
              )}
            </label>
            <Text color={colorStyle.color.secondary}>
              {t(
                'globalSetting.notificationEmailSettings.notificationBehavior.tip'
              )}
            </Text>
          </Box>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            {t(
              'globalSetting.notificationEmailSettings.signatureContent.title'
            )}
          </Text>
        </Flex>

        <Flex alignItems="center">
          <Switch
            ref={textareaChildRef}
            pr="1x"
            id="signatureContent"
            checked={signatureContentStatus}
            onChange={onChangeSignatureContentStatus}
          />
          <label htmlFor="email-alerts">
            {t(
              'globalSetting.notificationEmailSettings.signatureContent.description'
            )}
          </label>
        </Flex>
        <Flex m="16px 0px">
          <TextareaWithValid
            ref={textareaChildRef}
            onChange={setSignatureContent}
            value={signatureContent}
            disabled={!signatureContentStatus}
            validates={textareaValidates}
            width="436px"
            height="135px"
            maxLength={1024}
          />
        </Flex>
        {props.customizedDisplayNameEnabled && (
          <Box>
            <Flex m="16px 0px">
              <Text>
                {t(
                  'globalSetting.notificationEmailSettings.senderDisplayName.title'
                )}
              </Text>
            </Flex>
            <Flex alignItems="center">
              <Switch
                ref={inputChildRef}
                pr="1x"
                id="senderDisplayName"
                checked={senderDisplayNameStatus}
                onChange={onChangeSenderDisplayNameStatus}
              />
              <label htmlFor="email-alerts">
                {t(
                  'globalSetting.notificationEmailSettings.senderDisplayName.description'
                )}
              </label>
            </Flex>
            <Flex m="16px 0px">
              <InputWithValid
                ref={inputChildRef}
                onChange={setSenderDisplayName}
                validates={inputValidates}
                width="436px"
                maxLength={32}
                value={senderDisplayName}
              />
            </Flex>
          </Box>
        )}
        <Flex
          position="fixed"
          pl="5x"
          bottom={0}
          left={270 + siderWidth}
          right={0}
          background={colorStyle.background.secondary}
          height={64}
          align="center"
          justify="flex-start"
        >
          <Button
            variant="primary"
            onClick={onSave}
            disabled={!props.withEditPermission}
          >
            {t('common.action.label.save')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};
export default React.memo(NotificationEmailSettings);
